import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Text, List, Button, Icon, iconWeight } from '@nula/core-ui';
import styles from './event-row.module.css';

const eventRow = cva(styles.base);

// ── Types ──────────────────────────────────────────────────────────────────

interface EventRowProps {
  /** Display time string (e.g. "9:00 AM" or "All day") */
  time: string;
  /** Event title */
  title: string;
  /** CSS color string or token for the left color bar */
  color: string;
  /** Optional trailing icon node (e.g. linked-note icon) */
  icon?: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Highlight as the event happening right now */
  isNow?: boolean;
  /** When provided, renders a "Join" button (e.g. for events with a meeting link) */
  onJoin?: () => void;
  /** Additional CSS class */
  className?: string;
}

// ── EventRow ───────────────────────────────────────────────────────────────

/**
 * EventRow — shared event row shape used in CalendarPage (mobile list) and
 * HomeCalendarWidget. Renders a List.Item with a colored bar, time label,
 * title, and optional trailing icon. The currently-happening event can be
 * highlighted via `isNow`, and a "Join" button shown via `onJoin`.
 */
export function EventRow({ time, title, color, icon, onClick, isNow, onJoin, className }: EventRowProps) {
  return (
    <List.Item
      onClick={onClick}
      size="md"
      radius="md"
      className={clsx(eventRow(), isNow && styles.now, className)}
    >
      {/* Color bar */}
      <Flex
        className={styles.colorBar}
        style={{ '--event-color': color } as React.CSSProperties}
      />
      <Text size="xs" className={clsx(styles.timeLabel, isNow && styles.timeLabelNow)}>{time}</Text>
      <Flex align="center" gap="xs" className={styles.titleArea}>
        <Text size="sm" className={styles.title}>{title}</Text>
        {icon && <span className={styles.iconSlot}>{icon}</span>}
      </Flex>
      {onJoin && (
        <Button
          size="sm"
          pill
          variant="default"
          aria-label="Join meeting"
          className={styles.joinButton}
          onClick={(e) => {
            e.stopPropagation();
            onJoin();
          }}
          icon={<Icon name="videocam" size={14} weight={iconWeight.light} />}
        >
          Join
        </Button>
      )}
    </List.Item>
  );
}
